/** Перенос `domain/models/api_response_model/api_response_model.dart`. */

import { z } from 'zod';

import { apiResponseStatusSchema, type ApiResponseStatus } from '@/domain/models/apiResponseStatus';

export interface ApiResponseModel<T> {
  readonly status: ApiResponseStatus;
  readonly detail: string | null;
  readonly message: string | null;
  readonly data: T | null;
}

/**
 * В Dart сюда передавался `Response<dynamic>` из dio. Слою моделей нужно
 * только тело ответа, поэтому контракт сведён к одному полю — под него
 * подходит любой ответ самописного HTTP-слоя.
 */
export interface ApiResponseSource {
  readonly data: unknown;
}

/** `T? Function(Object item)` из Dart — разбор содержимого поля `data`. */
export type ApiResponseItemParser<T> = (item: unknown) => T | null;

/**
 * Конверт ответа. Лишние ключи zod отбрасывает — как и Dart, который просто
 * не читал их из `Map`.
 */
const apiResponseEnvelopeSchema = z.object({
  status: apiResponseStatusSchema,
  detail: z.string().nullish(),
  message: z.string().nullish(),
  // `.optional()` обязателен: в zod 4 голый `z.unknown()` требует, чтобы ключ
  // ПРИСУТСТВОВАЛ в объекте, а Dart читал `json['data']`, и его отсутствие
  // было законным. Без этого тело ошибки вида `{"status":"error","detail":…}`
  // не разбиралось, и сообщение сервера не доезжало до `AppException`.
  data: z.unknown().optional(),
});

export function apiResponseModelFromJson<T>(
  json: unknown,
  onJsonItem?: ApiResponseItemParser<T>,
): ApiResponseModel<T> {
  const envelope = apiResponseEnvelopeSchema.parse(json);
  const data = envelope.data;
  return {
    status: envelope.status,
    detail: envelope.detail ?? null,
    message: envelope.message ?? null,
    // `data == null ? null : onJsonItem?.call(data)`: без парсера данных нет.
    data: data === null || data === undefined ? null : (onJsonItem?.(data) ?? null),
  };
}

export function apiResponseModelFromResponse<T>(
  response: ApiResponseSource,
  onJsonItem?: ApiResponseItemParser<T>,
): ApiResponseModel<T> {
  return apiResponseModelFromJson<T>(response.data, onJsonItem);
}

/**
 * `tryFromResponse`. Ошибка здесь гасится намеренно и так же, как в Dart:
 * это разбор тела ошибочного ответа, у которого формат может быть любым, —
 * не разобрали, значит подробностей об ошибке просто нет.
 */
export function apiResponseModelTryFromResponse<T>(
  response: ApiResponseSource,
  onJsonItem?: ApiResponseItemParser<T>,
): ApiResponseModel<T> | null {
  try {
    return apiResponseModelFromResponse<T>(response, onJsonItem);
  } catch {
    return null;
  }
}
